import type { SessionCommentAttributes, SessionTagAttributes } from '@/server/models'

import SessionBreakoutQ from '@/queries/SessionBreakoutQ'
import type { UserContextType } from '@/client/context/user'
import { SQL, type SQLFragment } from '@/client/query'
import { getAuditorTagsUserCanAccess } from '@/client/services/sessionAudit/tags'
import { AUDITOR_TAGS, MANUALLY_ASSIGNED_TAGS, MENTOR_TAGS } from '@/client/services/tags/constants'
import type { SessionTag as SessionTagType } from '@/client/services/tags/types'

import {
  type AssociationDefinitions,
  type GroupBy,
  type IncludeAssociations,
  includeAssociationsToSql,
  makeQueryModelV2,
  type QueryOrder,
  queryGroupByToSQL,
  queryLimitToSQL,
  queryOrderToSQL,
} from './common'
import SessionQ from './SessionQ'

const SessionTagQ = {
  ...makeQueryModelV2<SessionTagAttributes>('session_tag')([
    'id',
    'user_id',
    'session_id',
    'session_breakout_id',
    'tag',
    'updated_at',
    'created_at',
    'deleted_at',
  ]),

  associations: (): AssociationDefinitions => ({
    Session: {
      query: SessionQ,
      foreignKey: SessionQ.id,
      sourceKey: SessionTagQ.session_id,
    },
    SessionBreakout: {
      query: SessionBreakoutQ,
      foreignKey: SessionBreakoutQ.id,
      sourceKey: SessionTagQ.session_breakout_id,
    },
  }),

  hasTag: (tag: SessionTagType): SQLFragment<boolean> => SQL`${SessionTagQ.tag} = ${tag}`,

  hasAnyTag: (tags: SessionTagType[]): SQLFragment<boolean> =>
    SQL`${SessionTagQ.tag} = ANY(${tags}::text[])`,

  where: (
    {
      paranoid = true,
      userId,
      sessionId,
      sessionBreakoutId,
      tag,
      include = [],
      groupBy = null,
      order = null,
      limit = null,
    }: {
      paranoid?: boolean
      userId?: number | null | SQLFragment<number>
      sessionId?: number | null | SQLFragment<number>
      sessionBreakoutId?: number | null | SQLFragment<number>
      tag?: SessionTagType | null
      include?: IncludeAssociations
      groupBy?: GroupBy | null
      order?: QueryOrder | null
      limit?: number | null
    },
    customWhere: SQLFragment<unknown> | null = null
  ): SQLFragment<SessionCommentAttributes> => SQL`
        FROM ${SessionTagQ}
        ${includeAssociationsToSql(SessionTagQ, include)}
        WHERE
            ${paranoid ? SQL`${SessionTagQ.deleted_at} IS NULL AND` : SQL``}
            ${userId != null ? SQL`${SessionTagQ.user_id} = ${userId} AND` : SQL``}
            ${sessionId != null ? SQL`${SessionTagQ.session_id} = ${sessionId} AND` : SQL``}
            ${sessionBreakoutId != null ? SQL`${SessionTagQ.session_breakout_id} = ${sessionBreakoutId} AND` : SQL``}
            ${tag != null ? SQL`${SessionTagQ.tag} = ${tag} AND` : SQL``}
            ${customWhere != null ? customWhere : SQL`TRUE`}
        ${queryGroupByToSQL(groupBy)}
        ${queryOrderToSQL(order)}
        ${queryLimitToSQL(limit)}
    `,

  isAuditorTagUserCanAccess: (user: UserContextType): SQLFragment<boolean> =>
    SQL`${SessionTagQ.tag} = ANY(${getAuditorTagsUserCanAccess(user)})`,

  isAuditorOrMentorTag: (): SQLFragment<boolean> =>
    SQL`${SessionTagQ.tag} = ANY(${[...AUDITOR_TAGS, ...MENTOR_TAGS]})`,

  isManuallyGeneratedTag: (): SQLFragment<boolean> =>
    SQL`${SessionTagQ.tag} = ANY(${[...MANUALLY_ASSIGNED_TAGS]})`,
}

export default SessionTagQ
